/**
 * Hook que reproduce un tono de ringback (llamada sonando) usando Web Audio API.
 *
 * Tono estándar europeo (ITU-T):
 *   - Frecuencia: 425 Hz (sinusoidal pura)
 *   - Cadencia: 1 segundo ON, 4 segundos OFF (ciclo de 5s)
 *
 * Se activa durante los estados 'connecting' y 'ringing' de la llamada WebRTC.
 * Se detiene automáticamente al entrar en 'active', 'done', 'error' o 'idle'.
 *
 * Esto da al agente la misma experiencia que una llamada telefónica real:
 * sabe que la llamada está cursándose, si suena, y cuándo contestan.
 */

import { useCallback, useRef } from 'react';
import { logger } from '@/utils/logger';

// ─── Constantes del tono europeo ────────────────────────────────────────────

/** Frecuencia del tono de ringback europeo (Hz) */
const RINGBACK_FREQ = 425;
/** Duración del tono ON (segundos) */
const TONE_ON = 1.0;
/** Duración del silencio OFF (segundos) */
const TONE_OFF = 4.0;
/** Volumen del tono (0.0 a 1.0) — no muy alto para no molestar */
const TONE_VOLUME = 0.15;

export interface UseRingbackToneReturn {
  /** Inicia la reproducción del ringback tone en bucle */
  start: () => void;
  /** Detiene la reproducción y libera recursos */
  stop: () => void;
  /** true si el tono está reproduciéndose */
  isPlaying: boolean;
}

export function useRingbackTone(): UseRingbackToneReturn {
  const ctxRef = useRef<AudioContext | null>(null);
  const oscRef = useRef<OscillatorNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isPlayingRef = useRef(false);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (oscRef.current) {
      try {
        oscRef.current.stop();
      } catch {
        // ya parado
      }
      oscRef.current.disconnect();
      oscRef.current = null;
    }
    if (gainRef.current) {
      gainRef.current.disconnect();
      gainRef.current = null;
    }
    if (ctxRef.current) {
      ctxRef.current.close().catch(() => {});
      ctxRef.current = null;
    }
    isPlayingRef.current = false;
    logger.info('[useRingbackTone] Tono detenido');
  }, []);

  const start = useCallback(() => {
    // Evitar doble inicio
    if (isPlayingRef.current) return;

    try {
      const ctx = new AudioContext();
      ctxRef.current = ctx;
      isPlayingRef.current = true;

      // Resumir AudioContext si está suspendido (política autoplay del navegador).
      // Chrome/Safari suspenden el contexto si no hay interacción previa del usuario.
      // En nuestro caso, el agente siempre hace clic en "Llamar" antes, pero por
      // seguridad lo resumimos explícitamente.
      const initAudio = () => {
        // Verificar que no se llamó stop() mientras esperábamos resume()
        if (!isPlayingRef.current || ctxRef.current !== ctx) return;

        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(RINGBACK_FREQ, ctx.currentTime);

        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0, ctx.currentTime); // empieza silencio

        osc.connect(gain);
        gain.connect(ctx.destination);

        oscRef.current = osc;
        gainRef.current = gain;

        osc.start();

        // ── Cadencia: 1s ON, 4s OFF ──────────────────────────────────
        // Primer tono inmediato
        gain.gain.setValueAtTime(TONE_VOLUME, ctx.currentTime);
        gain.gain.setValueAtTime(0, ctx.currentTime + TONE_ON);

        const cycleDuration = (TONE_ON + TONE_OFF) * 1000; // 5000ms

        // Programar ciclos sucesivos
        intervalRef.current = setInterval(() => {
          if (!ctxRef.current || !gainRef.current) return;
          const now = ctxRef.current.currentTime;
          gainRef.current.gain.cancelScheduledValues(now);
          gainRef.current.gain.setValueAtTime(TONE_VOLUME, now);
          gainRef.current.gain.setValueAtTime(0, now + TONE_ON);
        }, cycleDuration);

        logger.info('[useRingbackTone] Tono iniciado (425Hz, 1s on / 4s off)');
      };

      if (ctx.state === 'suspended') {
        ctx.resume().then(initAudio).catch(() => {
          logger.warn('[useRingbackTone] No se pudo resumir AudioContext');
          isPlayingRef.current = false;
        });
      } else {
        initAudio();
      }
    } catch (e) {
      logger.warn('[useRingbackTone] No se pudo iniciar el tono:', e);
      isPlayingRef.current = false;
    }
  }, []);

  return {
    start,
    stop,
    get isPlaying() {
      return isPlayingRef.current;
    },
  };
}
